import { useState } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import type { PlayerTabs, SeasonType } from '../Routing/urls'
import {
  YEAR_MIN,
  YEAR_MAX,
  CURRENT_YEAR,
  EVENTS_YEAR,
} from '../Config/League/league.config'
import {
  PlayerDataGQL,
  PlayersStatsData,
  TeamStats,
  GameLog,
  PlayerTeam,
  EventGQL,
} from '../Api'
type StatsFilterList = PlayersStatsData | TeamStats
/**
 * Get current teamCode from window.teamCode variable
 * or from route params if exists. Empty string if
 * no code found.
 */
export const useTeamName = (): string => {
  const rparams: Record<string, string | undefined> = useParams()
  if (rparams.teamName) {
    return rparams.teamName
  }
  return ''
}

/**
 *
 * Hook for component manual component refresh
 */
export const useForceRefresh = () => {
  const [state, setState] = useState<number>(0)
  const refresh = () => {
    setState(state < 100 ? state + 1 : 0)
  }
  return refresh
}

export type PlayerProfileUrlParams = {
  slug: string
  tab: PlayerTabs
  type: SeasonType
  year: number
  yr?: number
  statYear?: number
  eventsYear?: number
}

export type MatchupUrlParams = {
  officialId: string
}
export type QuestionUrlParams = {
  questionId?: string
  groupId?: string
}

export type EarlyAccessUrlParams = {
  source: string
}

export type SingleVideoUrlParams = {
  mediaId: string
}
export type VideoPlaylistUrlParams = {
  playlistId: string
  tagId: string
  eventSlug: string
}

function parseTab(tab: any): PlayerTabs {
  switch (tab) {
    case 'media':
      return 'media'
    case 'stats':
      return 'stats'
    default:
      return 'profile'
  }
}

function parseYear(year: any): number {
  if (!year || typeof year !== 'string') return CURRENT_YEAR
  const y = parseInt(year)
  if (y < YEAR_MIN) return YEAR_MIN
  if (y > YEAR_MAX) return CURRENT_YEAR
  return y
}

function parseSeasonType(season: any): SeasonType {
  return season === 'post'
    ? 'post'
    : season === 'champseries'
    ? 'champseries'
    : 'regular'
}

/**
 * Get current player name and other params from URL
 */
export const usePlayerUrlParams = (): PlayerProfileUrlParams => {
  const rparams: Record<string, string | undefined> = useParams()
  const location = useLocation()
  const qparams = location.search
    ? Object.fromEntries(new URLSearchParams(location.search))
    : {}
  const result: PlayerProfileUrlParams = {
    slug: rparams.slug || '',
    tab: parseTab(rparams.tab),
    year: qparams.yr ? parseYear(qparams.yr) : parseYear(qparams.year),
    type: parseSeasonType(qparams.type),
    eventsYear: qparams.eventsYear ? parseYear(qparams.eventsYear) : EVENTS_YEAR,
  }
  return result
}

/**
 * Filter stats for players that played in PLL only
 */
export const filterPLLStats = (list: StatsFilterList[]) => {
  return list.filter((item) => item.statLeague === 'PLL')
}

/**
 * Get event questionId from URL
 */
export const useQuestionUrlParams = (): QuestionUrlParams => {
  const rparams: Record<string, string | undefined> = useParams()
  const result: QuestionUrlParams = {
    questionId: rparams.questionId || '',
  }
  return result
}

/**
 * Get event questionId from URL
 */
export const useGroupQuestionUrlParams = (): QuestionUrlParams => {
  const rparams: Record<string, string | undefined> = useParams()
  const result: QuestionUrlParams = {
    groupId: rparams.groupId || '',
  }
  return result
}

/**
 * Get event officialID from URL
 */
export const useMatchupUrlParams = (): MatchupUrlParams => {
  const rparams: Record<string, string | undefined> = useParams()
  const result: MatchupUrlParams = {
    officialId: rparams.officialId || '',
  }
  return result
}

/**
 * Get single video id from URL
 */
export const useSingleVideoUrlParams = (): SingleVideoUrlParams => {
  const rparams: Record<string, string | undefined> = useParams()
  const result: SingleVideoUrlParams = {
    mediaId: rparams.mediaId || '',
  }
  return result
}

/**
 * Get video playlist id from URL
 */
export const useVideoPlaylistUrlParams = (): VideoPlaylistUrlParams => {
  const rparams: Record<string, string | undefined> = useParams()
  const result: VideoPlaylistUrlParams = {
    playlistId: rparams.playlistId || '',
    tagId: rparams.tag || '',
    eventSlug: rparams.eventSlug || '',
  }
  return result
}

/**
 * Get event questionId from URL
 */
export const useEarlyAccessUrlParams = (): string | null => {
  const location = useLocation()
  const qParams = new URLSearchParams(location.search)
  return qParams.get('source')
}

export const convertAllEvents = (
  player: PlayerDataGQL,
  events: EventGQL[],
  teams?: PlayerTeam[],
  yearTeam?: string
): GameLog[] => {
  return events.map((e) => {
    if (!yearTeam) {
      yearTeam = player.currentTeam.officialId
    }
    let opponentId =
      yearTeam === e.awayTeam.officialId ? e.homeTeam.officialId : e.awayTeam.officialId

    if (
      player.currentTeam.officialId !== e.awayTeam.officialId &&
      player.currentTeam.officialId !== e.homeTeam.officialId
    ) {
      const lastTeam = teams?.find(
        (team) =>
          team.officialId === e.awayTeam.officialId ||
          team.officialId === e.homeTeam.officialId
      )
      if (lastTeam) {
        opponentId =
          lastTeam.officialId === e.awayTeam.officialId
            ? e.homeTeam.officialId
            : e.awayTeam.officialId
      }
    }

    const seasonSegment =
      e.seasonSegment === 'allstar' ? 3 : e.seasonSegment === 'post' ? 2 : 1

    const gameLog: GameLog = {
      officialId: player.officialId,
      teamId: player.currentTeam.officialId,
      opponent: opponentId,
      gamesPlayed: e.playerEventStats?.gamesPlayed || 0,
      goals: e.playerEventStats?.goals || 0,
      assists: e.playerEventStats?.assists || 0,
      points: e.playerEventStats?.points || 0,
      turnovers: e.playerEventStats?.turnovers || 0,
      shots: e.playerEventStats?.shots || 0,
      shotPct: e.playerEventStats?.shotPct || 0,
      shotsOnGoal: e.playerEventStats?.shotsOnGoal || 0,
      shotsOnGoalPct: e.playerEventStats?.shotsOnGoalPct || 0,
      gamesStarted: e.playerEventStats?.gamesStarted || 0,
      onePointGoals: e.playerEventStats?.onePointGoals || 0,
      twoPointGoals: e.playerEventStats?.twoPointGoals || 0,
      saves: e.playerEventStats?.saves || 0,
      savePct: e.playerEventStats?.savePct || 0,
      faceoffPct: e.playerEventStats?.faceoffPct || 0,
      foRecord: e.playerEventStats?.foRecord || '',
      causedTurnovers: e.playerEventStats?.causedTurnovers || 0,
      groundBalls: e.playerEventStats?.groundBalls || 0,
      scoresAgainst: e.playerEventStats?.scoresAgainst || 0,
      powerPlayGoals: e.playerEventStats?.powerPlayGoals || 0,
      numPenalties: e.playerEventStats?.numPenalties || 0,
      touches: e.playerEventStats?.touches || 0,
      totalPasses: e.playerEventStats?.totalPasses || 0,
      twoPointGoalsAgainst: e.playerEventStats?.twoPointGoalsAgainst || 0,
      eventId: e.slugname,
      startTime: e.startTime,
      seasonSegment: seasonSegment,
    }

    return gameLog
  })
}
